// Humanoid spec — plain JSON, deterministic, no box3d-web or three import.
import {
  HUMANOID_JOINT_DEFS,
  HUMANOID_JOINT_NAMES,
  boneMassKg,
  type FootLayout,
  type HumanoidJointName,
  type JointLayout,
  type JointLimitPreset,
  type ProportionPreset,
} from './humanoidBones';

export type { FootLayout, JointLayout };

export const HUMANOID_SPEC_SCHEMA_VERSION = 1 as const;

/** Adjacent-body mass ratio above which resolveHumanoidSpec throws. See the
 *  hips/spine re-split comment in humanoidBones.ts for why 5:1 is achievable. */
export const MAX_ADJACENT_MASS_RATIO = 5;

export interface JointTuningOverride {
  /** Spherical-joint soft-limit spring stiffness (Hz). Ignored for revolute joints
   *  (box3d-web's RevoluteJointOptions has no spring field). */
  springHertz?: number;
  springDampingRatio?: number;
}

export type Vec3Tuple = readonly [number, number, number];
export type QuatTuple = readonly [number, number, number, number];

export interface HumanoidSpec {
  schemaVersion: typeof HUMANOID_SPEC_SCHEMA_VERSION;
  height: number;
  mass: number;
  proportionPreset: ProportionPreset;
  limitPreset: JointLimitPreset;
  jointTuning: Partial<Record<HumanoidJointName, JointTuningOverride>>;
  position: Vec3Tuple;
  rotation: QuatTuple;
  friction: number;
  restitution: number;
  linearDamping: number;
  angularDamping: number;
  /**
   * Foot box orientation/layout. `default` keeps the legacy lateral-long foot
   * used by certified balance scenarios. `anatomical` is muscleBalance-only:
   * ~10×26 cm sagittal foot with heel-biased ankle (same volume/mass).
   */
  footLayout: FootLayout;
  /**
   * Joint topology layout (checkpoint 20). `default` = revolute ankles (X-only).
   * `muscleBalance` = spherical ankles (pitch+roll); must only be used for the
   * muscleBalance scenario so balance/etc stay bit-compatible.
   */
  jointLayout: JointLayout;
  /** Deterministic seed for crowd jitter (see createHumanoidCrowd / seededUnit). */
  seed: number;
  /** Fraction (0..1) of uniform random scale jitter applied on top of `height`
   *  when spawned via createHumanoidCrowd. Ignored by a single createHumanoid. */
  scaleJitter: number;
}

export type HumanoidSpecInput = Partial<Omit<HumanoidSpec, 'schemaVersion'>>;

const DEFAULT_SPEC: HumanoidSpec = {
  schemaVersion: HUMANOID_SPEC_SCHEMA_VERSION,
  height: 1.8,
  mass: 75,
  proportionPreset: 'average',
  limitPreset: 'game',
  jointTuning: {},
  position: [0, 0, 0],
  rotation: [0, 0, 0, 1],
  friction: 0.6,
  restitution: 0,
  linearDamping: 0.1,
  angularDamping: 0.3,
  footLayout: 'default',
  jointLayout: 'default',
  seed: 0,
  scaleJitter: 0,
};

export function resolveHumanoidSpec(input: HumanoidSpecInput = {}): HumanoidSpec {
  const resolved: HumanoidSpec = {
    schemaVersion: HUMANOID_SPEC_SCHEMA_VERSION,
    height: input.height ?? DEFAULT_SPEC.height,
    mass: input.mass ?? DEFAULT_SPEC.mass,
    proportionPreset: input.proportionPreset ?? DEFAULT_SPEC.proportionPreset,
    limitPreset: input.limitPreset ?? DEFAULT_SPEC.limitPreset,
    jointTuning: cloneJointTuning(input.jointTuning ?? DEFAULT_SPEC.jointTuning),
    position: input.position ? cloneVec3(input.position) : cloneVec3(DEFAULT_SPEC.position),
    rotation: input.rotation ? cloneQuat(input.rotation) : cloneQuat(DEFAULT_SPEC.rotation),
    friction: input.friction ?? DEFAULT_SPEC.friction,
    restitution: input.restitution ?? DEFAULT_SPEC.restitution,
    linearDamping: input.linearDamping ?? DEFAULT_SPEC.linearDamping,
    angularDamping: input.angularDamping ?? DEFAULT_SPEC.angularDamping,
    footLayout: input.footLayout ?? DEFAULT_SPEC.footLayout,
    jointLayout: input.jointLayout ?? DEFAULT_SPEC.jointLayout,
    seed: input.seed ?? DEFAULT_SPEC.seed,
    scaleJitter: input.scaleJitter ?? DEFAULT_SPEC.scaleJitter,
  };
  validateHumanoidSpec(resolved);
  return resolved;
}

export function validateHumanoidSpec(spec: HumanoidSpec): void {
  if (spec.schemaVersion !== HUMANOID_SPEC_SCHEMA_VERSION) {
    throw new RangeError(`Unsupported humanoid spec schema: ${String(spec.schemaVersion)}.`);
  }
  requireFiniteInRange('height', spec.height, 0.3, 3);
  requireFiniteInRange('mass', spec.mass, 5, 500);
  if (spec.proportionPreset !== 'average' && spec.proportionPreset !== 'heroic') {
    throw new RangeError('proportionPreset must be "average" or "heroic".');
  }
  if (spec.limitPreset !== 'game' && spec.limitPreset !== 'anatomical') {
    throw new RangeError('limitPreset must be "game" or "anatomical".');
  }
  if (spec.footLayout !== 'default' && spec.footLayout !== 'anatomical') {
    throw new RangeError('footLayout must be "default" or "anatomical".');
  }
  if (spec.jointLayout !== 'default' && spec.jointLayout !== 'muscleBalance') {
    throw new RangeError('jointLayout must be "default" or "muscleBalance".');
  }
  requireFiniteInRange('friction', spec.friction, 0, Number.POSITIVE_INFINITY);
  requireFiniteInRange('restitution', spec.restitution, 0, 1);
  requireFiniteInRange('linearDamping', spec.linearDamping, 0, Number.POSITIVE_INFINITY);
  requireFiniteInRange('angularDamping', spec.angularDamping, 0, Number.POSITIVE_INFINITY);
  requireFiniteInRange('scaleJitter', spec.scaleJitter, 0, 1);
  if (!Number.isFinite(spec.seed)) throw new RangeError('seed must be a finite number.');
  requireVec3('position', spec.position);
  requireVec3('rotation', spec.rotation.slice(0, 3) as unknown as Vec3Tuple);
  if (!Number.isFinite(spec.rotation[3])) throw new RangeError('rotation[3] (w) must be finite.');

  validateJointTuning(spec.jointTuning);
  validateAdjacentMassRatios(spec.mass);
}

function validateJointTuning(jointTuning: HumanoidSpec['jointTuning']): void {
  for (const [joint, tuning] of Object.entries(jointTuning)) {
    if (!HUMANOID_JOINT_NAMES.includes(joint as HumanoidJointName)) {
      throw new RangeError(`jointTuning contains unknown joint: ${joint}.`);
    }
    if (!tuning) continue;
    if (tuning.springHertz !== undefined) {
      requireFiniteInRange(`jointTuning.${joint}.springHertz`, tuning.springHertz, 0, Number.POSITIVE_INFINITY);
    }
    if (tuning.springDampingRatio !== undefined) {
      requireFiniteInRange(`jointTuning.${joint}.springDampingRatio`, tuning.springDampingRatio, 0, 10);
    }
  }
}

/** Adjacent = bodies directly connected by a joint (HUMANOID_JOINT_DEFS). Mass
 *  fractions are fixed in v1 (no per-bone override surface), so this currently
 *  validates the shared table itself; it stays load-bearing once per-bone mass
 *  overrides land. */
function validateAdjacentMassRatios(totalMassKg: number): void {
  for (const def of HUMANOID_JOINT_DEFS) {
    const parentMass = boneMassKg(def.parent, totalMassKg);
    const childMass = boneMassKg(def.child, totalMassKg);
    const ratio = Math.max(parentMass, childMass) / Math.min(parentMass, childMass);
    if (ratio > MAX_ADJACENT_MASS_RATIO) {
      throw new RangeError(
        `Adjacent mass ratio at joint "${def.name}" (${def.parent}/${def.child}) is ${ratio.toFixed(2)}:1, exceeding ${MAX_ADJACENT_MASS_RATIO}:1.`,
      );
    }
  }
}

function requireFiniteInRange(name: string, value: number, min: number, max: number): void {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${name} must be finite and within [${min}, ${max}]; got ${value}.`);
  }
}

function requireVec3(name: string, value: Vec3Tuple): void {
  if (!Array.isArray(value) || value.length !== 3 || !value.every((component) => Number.isFinite(component))) {
    throw new RangeError(`${name} must be a 3-tuple of finite numbers.`);
  }
}

function cloneVec3(value: Vec3Tuple): Vec3Tuple {
  return [value[0], value[1], value[2]];
}

function cloneQuat(value: QuatTuple): QuatTuple {
  return [value[0], value[1], value[2], value[3]];
}

function cloneJointTuning(
  value: HumanoidSpec['jointTuning'],
): HumanoidSpec['jointTuning'] {
  return JSON.parse(JSON.stringify(value)) as HumanoidSpec['jointTuning'];
}

export function serializeHumanoidSpec(spec: HumanoidSpec): string {
  validateHumanoidSpec(spec);
  return JSON.stringify(spec, null, 2);
}

export function parseHumanoidSpec(serialized: string): HumanoidSpec {
  let value: unknown;
  try {
    value = JSON.parse(serialized);
  } catch (error) {
    throw new SyntaxError(`Invalid humanoid spec JSON: ${(error as Error).message}`);
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError('Humanoid spec must be an object.');
  }
  const spec = value as HumanoidSpec;
  validateHumanoidSpec(spec);
  return JSON.parse(JSON.stringify(spec)) as HumanoidSpec;
}
